import { useCurrentMeeting } from "../contexts/currentMeeting";
import {
    ChatParticipantInfoSubscription,
    ChatParticipantInfoDocument,
    ChatParticipantInfoSubscriptionVariables,
} from "./queries-chatto.generated";
import { OmitTypeName } from "conference/types";
import { createContext, useContext, useState } from "react";
import { useChattoWsSubscription } from "conference/apollo-client/chatto/hooks";
import { createContextProvider } from "./widgets";

export type ParticipantStatus = OmitTypeName<
    ChatParticipantInfoSubscription["chatParticipantInfo"][number]["additionalInfo"]
> & {
    isHost: boolean;
};

export type ParticipantsStatus = Record<string, ParticipantStatus>;

export const defaultStatus: ParticipantStatus = {
    handRaised: false,
    isMuted: false,
    isHost: false,
    name: "",
    unmuteAllowed: true,
};

export const useParticipantsStatusFetcher = (): ParticipantsStatus => {
    const currentMeeting = useCurrentMeeting();
    const [status, setStatus] = useState<ParticipantsStatus>({});

    useChattoWsSubscription<ChatParticipantInfoSubscription, ChatParticipantInfoSubscriptionVariables>(
        ChatParticipantInfoDocument,
        {
            variables: {
                roomId: currentMeeting.chatRoomId,
            },
            onData: (options) => {
                const info = options.data.data.chatParticipantInfo.reduce((res, item) => {
                    res[item.participant] = {
                        ...item.additionalInfo,
                        isHost: item.isAdmin,
                    };
                    return res;
                }, {} as ParticipantsStatus);
                setStatus(info);
            },
        }
    );

    return status;
};

export const ParticipantsStatusContext = createContext(undefined);

export const useParticipantsStatus = (): ParticipantsStatus => useContext(ParticipantsStatusContext);

export const useParticipantStatus = (uri: string): ParticipantStatus => {
    const data = useParticipantsStatus();
    return data[uri] ?? defaultStatus;
};

export const ParticipantsStatusContextProvider = createContextProvider(
    ParticipantsStatusContext,
    useParticipantsStatusFetcher
);
